import type { Product } from "core/catalog/types";
import { parseError, useSubmitProps } from "core/shared";
import { action } from "framework/config/action";
import { URLS } from "framework/config/urls";
import { useState } from "react";
import { CartItem, CustomOptionRequest } from "../types";

export function useAddToCart({ onSuccess, onError }: useSubmitProps) {
  const [loading, setLoading] = useState(false);
  const submit = action(URLS.cart);

  const addToCart = async (
    productId: number,
    quantity: number = 1,
    customOptions?: CustomOptionRequest[],
  ) => {
    setLoading(true);
    const response = await submit({
      product: productId,
      quantity,
      customOptions,
      action: "add",
    });

    if (response.isOK) {
      onSuccess?.(response.data);
    } else {
      onError?.(parseError(response));
    }

    setLoading(false);
  };

  return { addToCart, loading };
}

export function useRemoveFromCart({ onSuccess, onError }: useSubmitProps) {
  const [loading, setLoading] = useState(false);
  const submit = action(URLS.cart);

  const removeFromCart = async (productId: number) => {
    setLoading(true);
    const response = await submit({
      product: productId,
      action: "remove",
    });

    if (response.isOK) {
      onSuccess?.(response.data);
    } else {
      onError?.(parseError(response));
    }

    setLoading(false);
  };

  return { removeFromCart, loading };
}

export function useUpdateQuantity({ onSuccess, onError }: useSubmitProps) {
  const [loading, setLoading] = useState(false);
  const submit = action(URLS.cart);

  const updateQuantity = async (productId: number, quantity: number) => {
    setLoading(true);

    const response = await submit({
      product: productId,
      quantity,
      action: "update",
    });

    if (response.isOK) {
      onSuccess?.(response.data);
      setLoading(false);
      return quantity;
    } else {
      onError?.(parseError(response));
      setLoading(false);
      return null;
    }
  };

  return { updateQuantity, loading };
}

export function useClearCart({ onSuccess, onError }: useSubmitProps) {
  const [loading, setLoading] = useState(false);
  const submit = action(URLS.cart);

  const clearCart = async () => {
    setLoading(true);
    const response = await submit({ action: "clear" });

    if (response.isOK) {
      onSuccess?.(response.data);
    } else {
      onError?.(parseError(response));
    }

    setLoading(false);
  };

  return { clearCart, loading };
}

export function useAddToCartQuantity(
  product: Product,
  initialQuantity: number = 1,
) {
  const [quantity, setQuantity] = useState(initialQuantity);

  const minQuantity = product.purchase?.minQuantity || 1;
  const maxQuantity =
    product.purchase?.maxQuantity || product.stock?.available || Infinity;

  const incrementQuantity = () => {
    if (quantity < maxQuantity) {
      setQuantity((q) => q + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > minQuantity) {
      setQuantity((q) => q - 1);
    }
  };

  return {
    minQuantity,
    maxQuantity,
    quantity,
    incrementQuantity,
    decrementQuantity,
  };
}

export function useCartItemQuantity(
  item: CartItem,
  { onSuccess, onError }: useSubmitProps,
) {
  const { updateQuantity, loading: updateQuantityLoading } = useUpdateQuantity({
    onSuccess,
    onError,
  });
  const { removeFromCart, loading: removeFromCartLoading } = useRemoveFromCart({
    onSuccess,
    onError,
  });

  const [quantity, setQuantity] = useState(item.quantity);

  const decrementOrRemoveItem = async () => {
    if (quantity === 1 || quantity === item.product.purchase?.minQuantity) {
      removeFromCart(item.id);
    } else {
      const newQuantity = await updateQuantity(item.id, quantity - 1);
      if (newQuantity) {
        setQuantity(newQuantity);
      }
    }
  };

  const removeItem = async () => {
    removeFromCart(item.id);
  };

  const incrementQuantity = async () => {
    const newQuantity = await updateQuantity(item.id, quantity + 1);
    if (newQuantity) {
      setQuantity(newQuantity);
    }
  };

  return {
    quantity,
    loading: removeFromCartLoading || updateQuantityLoading,
    decrementOrRemoveItem,
    removeItem,
    incrementQuantity,
  };
}
